import { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigation } from "@remix-run/react";
import { Menu, MenuProps, Layout } from "antd";

import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
// 确保导入 Ant Design 的样式
import "antd/dist/reset.css";
type MenuItem = {
  key: string;
  icon?: string;
  label: string;
  path?: string;
  children?: MenuItem[];
  hidden?: boolean;
  permCode?: string;
  active?: (pathname: string) => boolean;
};

const CLIENT_MENU_FILTER_KEYS: Record<string, string> = {
  "/pages/leads": "CLUE_RESOURCE_filterState",
  "/pages/customers": "CUSTOMER_RESOURCE_filterState",
  "/pages/vehicles": "CAR_RESOURCE_filterState",
};

// Import styles
import "~/styles/components/sidebar.less";
import { getAntIcon } from "~/config/customerMenu";
import { fetchUserPermissions, usePagePermission } from "~/hooks/usePagePermission";
import { useTabs } from "~/contexts/TabContext";

const { Sider } = Layout;

function useTabsCount(): number {
  try {
    return useTabs().tabs.length;
  } catch {
    return -1;
  }
}

// Types
type SidebarProps = {
  collapsed?: boolean;
  menuItems?: MenuItem[];
  selectedKeys?: string[];
  onCollapse?: (collapsed: boolean) => void;
  menuKey?: "client" | "admin";
  userInfos?: any;
};

// Convert menu items to Ant Design menu items
const getMenuItems = (
  items?: MenuItem[],
  onItemClick?: (key: string) => void,
  location?: Location, // 添加 location 参数
  pendingPath?: string,
  clickSelectedKeys?: string[],
  tabsCount?: number
): MenuProps["items"] => {
  if (!items) return [];

  return items
    .filter((item) => !item.hidden)
    .map((item) => {
      const isActive =
        location?.pathname === item.path && tabsCount !== 0;
      const isPending = pendingPath === item.path;
      const isClickSelected = clickSelectedKeys?.includes(item.key);
      const menuItem: any = {
        key: item.key,
        icon: getAntIcon(item?.icon),
        label: item.external ? (
          <a href={item.path} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>
            {item.label}
          </a>
        ) : item.path ? (
          <Link
            to={item.path}
            onClick={(e) => {
              if (isActive) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              e.stopPropagation();
              const filterKey = item.path
                ? CLIENT_MENU_FILTER_KEYS[item.path]
                : undefined;
              if (filterKey) {
                try {
                  localStorage.removeItem(filterKey);
                } catch {}
              }
              onItemClick?.(item.key);
            }}
            style={
              isActive || isPending || isClickSelected
                ? { color: "#1890ff", fontWeight: "bold" }
                : {}
            }
          >
            {item.label}
          </Link>
        ) : (
          item.label
        ),
      };

      if (item.children) {
        menuItem.children = getMenuItems(item.children, onItemClick, location, pendingPath, clickSelectedKeys, tabsCount);
      }

      return menuItem;
    });
};

// 递归查找当前 path 的所有父级 key
function findMenuOpenKeys(
  items: any[],
  path: string,
  parents: string[] = []
): string[] {
  for (const item of items) {
    if (item.hidden) continue;
    // 检查当前项是否匹配
    const isActive =
      typeof item.active === "function"
        ? item.active(path)
        : item.path === path;

    if (isActive) {
      return parents;
    }

    // 检查子项
    if (item.children) {
      const found = findMenuOpenKeys(item.children, path, [
        ...parents,
        item.key,
      ]);
      if (found.length) return found;
    }
  }
  return [];
}



const Sidebar: React.FC<Omit<SidebarProps, "user">> = ({
  collapsed = false,
  menuItems,
  onCollapse,
}) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const location = useLocation();
  const navigation = useNavigation();
  const pendingPath = navigation.location?.pathname;
  const tabsCount = useTabsCount();
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  // 本地状态跟踪用户点击的菜单项，实现立即选择效果
  const [clickSelectedKeys, setClickSelectedKeys] = useState<string[]>([]);

  // 获取用户权限
  const checkPermissions = async () => {
    try {
      const permissions = await fetchUserPermissions();
      setUserPermissions(permissions || []); // 确保权限数据始终是数组
    } catch (error) {
      console.error('Failed to load permissions:', error);
      setUserPermissions([]); // 出错时也设置为空数组
    } finally {
      setPermissionsLoaded(true);
    }
  };

  // 初始化时加载权限
  useEffect(() => {
    checkPermissions();
  }, []);

  // 路由变化时重新检查权限
  useEffect(() => {
    // 延迟检查，确保路由变化完成
    const timer = setTimeout(() => {
      checkPermissions();
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // 处理菜单项点击
  const handleMenuItemClick = useCallback((key: string) => {
    setClickSelectedKeys([key]);
  }, []);

  // 检查权限
  const hasPermission = useCallback((permCode?: string) => {
    if (!permCode) return true; // 如果没有设置权限码，默认显示
    if (!userPermissions) return false; // 如果权限数据为 null/undefined，返回 false
    return userPermissions.includes(permCode);
  }, [userPermissions]);

  // 生成菜单项
  const menuItemsList = useMemo(() => {
    if (!permissionsLoaded) {
      return []; // 返回空数组，显示加载中状态
    }

    // 过滤菜单项
    const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
      if (!items) return [];

      return items
        .filter(item => {
          // 如果菜单项被隐藏，直接过滤掉
          if (item.hidden) return false;

          // 检查权限
          if (item.permCode && !hasPermission(item.permCode)) {
            return false;
          }

          // 递归处理子菜单
          if (item.children) {
            const filteredChildren = filterMenuItems(item.children);
            // 如果子菜单全部被过滤掉，则当前菜单也不显示
            if (filteredChildren.length === 0) return false;
            item.children = filteredChildren;
          }

          return true;
        });
    };

    const filteredItems = menuItems ? filterMenuItems([...menuItems]) : [];
    return getMenuItems(filteredItems, handleMenuItemClick, location, pendingPath, clickSelectedKeys, tabsCount);
  }, [menuItems, location, pendingPath, permissionsLoaded, hasPermission, handleMenuItemClick, clickSelectedKeys, tabsCount]);

  // 当路由变化完成后，清空本地点击状态
  useEffect(() => {
    setClickSelectedKeys([]);
  }, [location.pathname]);

  // 路由变化时刷新菜单
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [location.pathname]);

  // 计算展开的菜单项
  const computedOpenKeys = useMemo(
    () => (menuItems ? findMenuOpenKeys(menuItems, location.pathname) : []),
    [menuItems, location.pathname]
  );

  const [openKeys, setOpenKeys] = useState<string[]>(computedOpenKeys);

  useEffect(() => {
    setOpenKeys(computedOpenKeys);
  }, [computedOpenKeys]);

  // 查找选中的菜单项
  const findSelectedKey = useCallback((items: MenuItem[]): string[] => {
    const currentPath = location.pathname;
    for (const item of items) {
      const isActive =
        typeof item.active === "function"
          ? item.active(currentPath)
          : item.path === currentPath;

      if (isActive) {
        return [item.key];
      }

      if (item.children) {
        const childKey = findSelectedKey(item.children);
        if (childKey.length > 0) {
          return [item.key, ...childKey];
        }
      }
    }
    return [];
  }, [location.pathname]);

  const selectedKeys = useMemo(() => {
    if (!Array.isArray(menuItems)) return [];
    // 如果有点击的菜单项，优先使用点击的
    if (clickSelectedKeys.length > 0) {
      return clickSelectedKeys;
    }
    return findSelectedKey(menuItems || []);
  }, [menuItems, findSelectedKey, clickSelectedKeys]);

  // 检查导航是否正在加载
  const isNavigating = navigation.state === "loading";

  // 权限加载中显示加载状态
  if (!permissionsLoaded) {
    return (
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        width={200}
        className="sider"
      >
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </div>
      </Sider>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      width={200}
      className="sider"
    >
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        items={menuItemsList}
        style={{ borderRight: 0 }}
      />
    </Sider>
  );
};


export default Sidebar;
